import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import apiRequestCall from "../../../../apiHelper/globalHelper";
import { all_routes } from "../../../../router/backendComponents/admin/all_routes";
import Success from "../../../../formHelper/success";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../core/data/redux/store";
import Select from "react-select";

type Id = string;
const newId = () => Math.random().toString(36).slice(2);

interface CompanyEntry {
  id: number;
  customer_name?: string;
  company_name?: string;
  classification_id: string;
  address?: string;
  mobile_no?: string;
  email_id: string;
  landline_no: string;
  city?: string;
  state_id?: number | null;
  other_state?: string | null;
  state?: { id?: number; name?: string } | null;
  country?: { id?: number; name?: string } | null;
}

interface Option {
  value: Id;
  label: string;
  [k: string]: any;
}

interface KV {
  id: number;
  name: string;
}

// RowItem now supports a custom department string when "Others" is selected
interface RowItem {
  id: Id;
  department: string; // stores numeric id as string when selected, or "" when using custom
  departmentCustom?: string; // stores typed custom department when Others selected
  customerName: string;
}

type FormError = {
  department_customers?: {
    [index: number]: { [field: string]: string | undefined };
  };
} & { [key: string]: string | undefined };

interface Props {
  onNext?: () => void;
  editId?: number | null;
}

interface Branch {
  id: Id;
  name: string;
}
interface CustomerDD {
  id: Id;
  company_name: string;
  owner_name: string;
  address: string;
  state_id: Id;
  other_state: any;
  city: any;
  mobile_no: any;
  email_id: any;
  landline_no: any;
  pin_code: any;
  customer_name: any;
  owner?: { id: Id; name: string } | null;
  country: any;
  state: any;
  gst_number: any;
}

/* -- Keep useIsMobile & usePrefersDark as in your original code -- */
/* (omitted here for brevity in this snippet — keep your existing implementations) */

const useIsMobile = (maxWidth = 767.98) => {
  const isClient = typeof window !== "undefined";
  const [isMobile, setIsMobile] = useState<boolean>(
    isClient ? window.innerWidth <= maxWidth : false
  );
  useEffect(() => {
    if (!isClient) return;
    const mq = window.matchMedia(`(max-width: ${maxWidth}px)`);
    const handler = (e: MediaQueryListEvent | MediaQueryList) =>
      setIsMobile("matches" in e ? e.matches : mq.matches);
    if (typeof mq.addEventListener === "function")
      mq.addEventListener("change", handler as any);
    else (mq as any).addListener(handler);
    setIsMobile(mq.matches);
    return () => {
      if (typeof mq.removeEventListener === "function")
        mq.removeEventListener("change", handler as any);
      else (mq as any).removeListener(handler);
    };
  }, [maxWidth, isClient]);
  return isMobile;
};

const usePrefersDark = (): boolean => {
  const get = () =>
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-color-scheme: dark)").matches;
  const [isDark, setIsDark] = useState<boolean>(get());
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (event: MediaQueryListEvent | MediaQueryList) => {
      const matches =
        typeof (event as any).matches === "boolean"
          ? (event as any).matches
          : mq.matches;
      setIsDark(matches);
    };
    if (typeof mq.addEventListener === "function")
      mq.addEventListener("change", handler as EventListener);
    else (mq as any).addEventListener?.("change", handler);
    setIsDark(mq.matches);
    return () => {
      if (typeof mq.removeEventListener === "function")
        mq.removeEventListener("change", handler as EventListener);
      else (mq as any).removeListener?.(handler);
    };
  }, []);
  return isDark;
};

const makeOptions = (
  list: Branch[] | CustomerDD[],
  labelKey = "name",
  extra: (i: any) => any = () => ({})
) =>
  (list as any[]).map((b: any) => ({
    value: b.id,
    label: b[labelKey],
    ...extra(b),
  }));

export default function CustomerFormResponsive({
  onNext,
  editId,
}: Props): JSX.Element {
  const isMobile = useIsMobile();
  const isDark = usePrefersDark();
  const backendRoute = all_routes.backend;

  // cache of latest fetched companies by id
  const latestFetchedRef = useRef<Record<number, CompanyEntry>>({});
  const [showSuccess, setShowSuccess] = useState(false);
  const [showMessage, setShowMessage] = useState("");
  const [companyList, setCompanyList] = useState<CustomerDD[]>([]);

  const [formData, setFormData] = useState<Record<string, any>>({
    id: "",
    owner_id: null,
    company_id: null,
    concern_person_name: "",
    designation: "",
    contact_details: "",
    phone_no: "",
    email_id: "",
    company_name: "No Company (Search ...)",
  });

  const [formError, setFormError] = useState<FormError>({});
  const [classificationList, setClassificationList] = useState<KV[]>([]);
  const profile = useSelector((s: RootState) => s.profile?.data);

  const [rows, setRows] = useState<RowItem[]>([
    { id: newId(), department: "", departmentCustom: "", customerName: "" },
  ]);

  // SSR-safe size state
  const isClient = typeof window !== "undefined";
  const [size, setSize] = useState(() => ({
    width: isClient ? window.innerWidth : 1024,
    height: isClient ? window.innerHeight : 768,
  }));

  useEffect(() => {
    if (!isClient) return;
    const handleResize = () =>
      setSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    handleResize();
    return () => window.removeEventListener("resize", handleResize);
  }, [isClient]);

  const handleRowChange = (id: Id, field: keyof RowItem, value: string) =>
    setRows((prev) =>
      prev.map((r) =>
        r.id === id
          ? {
              ...r,
              [field]: value,
            }
          : r
      )
    );

  const addRow = () =>
    setRows((prev) => [
      ...prev,
      { id: newId(), department: "", departmentCustom: "", customerName: "" },
    ]);

  const removeRow = (id: Id) =>
    setRows((prev) =>
      prev.length > 1 ? prev.filter((r) => r.id !== id) : prev
    );

  const inputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.currentTarget;

    setFormError((prev: any) => {
      const updated = { ...prev };
      if (updated[name]) delete updated[name];
      return updated;
    });

    setFormData((prev: any) => ({ ...prev, [name]: value }));
  };

  // payload builder — now sends department and department_custom for each row
  const buildPayload = (fd: Record<string, any>, rs: RowItem[]) => ({
    id: fd.id ?? null,
    company_id: fd.company_id ?? null,
    concern_person_name: fd.concern_person_name ?? "",
    designation: fd.designation ?? "",
    contact_details: fd.contact_details ?? "",
    phone_no: fd.phone_no ?? "",
    email_id: fd.email_id ?? "",
    departmentCustomers: rs.map((r) => ({
      department:
        r.department && !isNaN(Number(r.department))
          ? Number(r.department)
          : null,
      department_custom: r.departmentCustom ?? null,
      customer_name: r.customerName ?? "",
    })),
  });

  // populate
  const populateFromPayload = (payload: any) => {
    const d = payload?.data ?? payload;
    setFormData((prev: any) => ({
      ...prev,
      id: d.id ?? null,
      owner_id: d.owner_id ?? null,
      company_id: d.company_id ?? prev.company_id ?? null,
      company_name:
        d.company?.company_name ??
        d.customer_name ??
        prev.company_name ??
        "No Company",
      company_mobile: d.company_mobile ?? "",
      company_city: d.company_city ?? "",
      company_state: d.company_state ?? d.state?.name ?? d.other_state ?? "",
      company_country: d.company_country ?? d.country?.name ?? "",
      company_address: d.company_address ?? d.address ?? "",
      concern_person_name:
        d.concern_person_name ?? prev.concern_person_name ?? "",
      designation: d.designation ?? prev.designation ?? "",
      contact_details: d.contact_details ?? prev.contact_details ?? "",
      phone_no: d.phone_no ?? prev.phone_no ?? "",
      email_id: d.email_id ?? prev.email_id ?? "",
    }));

    const newRows: RowItem[] = (
      Array.isArray(d.department_customers) ? d.department_customers : []
    ).map((rc: any) => ({
      id: newId(),
      department: rc.department != null ? String(rc.department) : "",
      departmentCustom: rc.department_custom ?? "",
      customerName: rc.customer_name ?? "",
    }));
    setRows(
      newRows.length
        ? newRows
        : [
            {
              id: newId(),
              department: "",
              departmentCustom: "",
              customerName: "",
            },
          ]
    );
  };

  const capitalize = (s: string) =>
    s ? s.charAt(0).toUpperCase() + s.slice(1) : s;

  const parseErrors = (errors: Record<string, string>): FormError => {
    const result: FormError = {};
    for (const key in errors) {
      if (key.startsWith("department_customers")) {
        const parts = key.split(".");
        const index = Number(parts[1]);
        const field = parts[2];
        if (!isNaN(index) && field) {
          result.department_customers ??= {};
          result.department_customers[index] ??= {};
          const readableField = field.replace(/_/g, " ");
          result.department_customers[index][field] = `${capitalize(
            readableField
          )} field is required.`;
        }
      } else {
        result[key] = errors[key];
      }
    }
    return result;
  };

  // fetch companies
  const getCompanyList = useCallback(async () => {
    const result = await apiRequestCall("post", backendRoute.getCompanyDD);
    const raw = result?.data?.data;
    if (raw && typeof raw === "object") {
      setCompanyList(
        raw.map((item: any) => ({
          id: item.id,
          company_name: item.company_name || "",
          gst_number: item.gst_number || "",
          owner_name: item?.owner?.name || "",
          address: item.address || "",
          state_id: item.state_id,
          other_state: item?.other_state,
          city: item?.city,
          country: item?.country,
          state: item?.state,
          email_id: item?.email_id,
          pin_code: item?.pin_code,
          mobile_no: item?.mobile_no,
          landline_no: item?.landline_no,
          customer_name: item?.customer_name,
        }))
      );
    }
  }, [backendRoute.getCompanyDD]);

  const getLookup = async (endpoint: string) => {
    try {
      const r = await apiRequestCall("post", endpoint);
      const raw = r?.data?.data;
      if (raw && typeof raw === "object") {
        return Object.entries(raw).map(([id, name]) => ({
          id: +id,
          name: String(name),
        }));
      }
    } catch (err) {
      console.error(`Failed to fetch ${endpoint}`, err);
    }
    return [] as KV[];
  };

  const getAllLookups = async () => {
    try {
      const [classifications] = await Promise.all([
        getLookup(backendRoute.getClassificationDD),
      ]);
      setClassificationList(classifications);
    } catch (err) {
      console.error("Failed to fetch lookups:", err);
    }
  };

  const tryLoadCmpById = async (id?: number | null) => {
    try {
      const res = await apiRequestCall(
        "post",
        backendRoute.getExpansesDetails,
        {
          data: { id },
        }
      );
      if (res?.data) populateFromPayload(res.data);
    } catch (err) {
      console.error("Failed to load company details:", err);
    }
  };

  const companyOptions = useMemo(
    () =>
      makeOptions(companyList, "company_name", (b: CustomerDD) => ({
        address: b.address,
        owner_name: b.owner_name,
        state_id: b.state_id,
        other_state: b.other_state,
        city: b.city,
        mobile_no: b.mobile_no,
        email_id: b.email_id,
        landline_no: b.landline_no,
        pin_code: b.pin_code,
        customer_name: b.customer_name,
        gst_number: b.gst_number,
        country: b.country?.name,
        state: b.state?.name,
      })),
    [companyList]
  );

  const handleSelectChange = useCallback((name: string, selected: any) => {
    setFormError((prev) => {
      const u = { ...prev };
      delete (u as any)[name];
      return u;
    });

    if (name === "company_id") {
      const updates: any = {
        company_id: selected?.value ?? null,
        company_name: selected?.label || null,
        gst_number: selected?.gst_number || null,
        state: selected?.state,
        country: selected?.country,
        billing_address: selected?.address || null,
        billing_city: selected?.city || null,
        billing_state_id: selected?.state_id || "",
        billing_mobile: selected?.mobile_no || "",
        billing_email: selected?.email_id || "",
        billing_landline: selected?.landline_no || "",
        billing_pin_code: selected?.pin_code || "",
        contact_person: selected?.customer_name || "",
        shipping_address: selected?.address || null,
        shipping_city: selected?.city || null,
        shipping_state_id: selected?.state_id || "",
      };

      if (selected?.other_state != null && selected?.other_state != "") {
        updates.billing_other_state = selected.other_state;
        updates.state = selected.other_state;
      }

      setFormError((prev) => {
        const u = { ...prev };
        Object.keys(updates).forEach((k) => delete (u as any)[k]);
        return u;
      });

      setFormData((prev: any) => ({ ...prev, ...updates }));
      return;
    }

    if (name === "billing_state_id") {
      setFormData((prev: any) => ({
        ...prev,
        state: selected?.label,
        billing_state_id: selected?.value ?? null,
      }));
      return;
    }

    setFormData((prev: any) => ({
      ...prev,
      [name]: selected?.value ?? null,
    }));
  }, []);

  const selectedOption = useCallback((opts: Option[], id?: Id | number) => {
    if (id == null) return null;

    return opts.find((o) => Number(o.value) === Number(id)) ?? null;
  }, []);

  useEffect(() => {
    void getAllLookups();
    tryLoadCmpById(editId);
  }, [backendRoute.getExpansesDetails, editId]);

  // compute the id for the "Others" classification if present
  const othersId = useMemo(
    () =>
      classificationList.find(
        (k) => k.name && k.name.toLowerCase().trim() === "others"
      )?.id ?? null,
    [classificationList]
  );

  const classificationOptionsJSX = classificationList.map((k) => (
    <option key={k.id} value={String(k.id)}>
      {k.name}
    </option>
  ));

  const mobileCardStyle: React.CSSProperties = {
    border: isDark
      ? "1px solid rgba(255,255,255,0.04)"
      : "1px solid rgba(0,0,0,0.06)",
    padding: 12,
    borderRadius: 8,
    marginBottom: 12,
    background: isDark ? "rgba(255,255,255,0.02)" : "#fff",
  };

  const actionButtonStyle: React.CSSProperties = isMobile
    ? { minWidth: 44, height: 40, padding: "8px 10px", boxSizing: "border-box" }
    : { minWidth: 36, height: 34, padding: "6px 10px" };

  const rowElements = rows.map((row, index) => (
    <tr key={row.id}>
      <td
        className="border p-2"
        data-label="Select Department"
        style={{ verticalAlign: "middle", width: "40%" }}
      >
        {String(row.department) === String(othersId) ? (
          <input
            type="text"
            className="form-control"
            placeholder="Type department"
            value={row.departmentCustom ?? ""}
            onChange={(e) =>
              handleRowChange(row.id, "departmentCustom", e.target.value)
            }
          />
        ) : (
          <select
            className="form-select"
            value={row.department || ""}
            onChange={(e) => {
              const val = e.target.value;

              handleRowChange(row.id, "department", val);

              if (String(val) !== String(othersId)) {
                handleRowChange(row.id, "departmentCustom", "");
              }
            }}
          >
            <option value="">Select Department</option>
            {classificationOptionsJSX}
          </select>
        )}
      </td>

      <td
        className="border p-2"
        data-label="Company Name"
        style={{ verticalAlign: "middle" }}
      >
        <input
          type="text"
          className="form-control"
          placeholder="Enter company name"
          value={row.customerName}
          onChange={(e) =>
            handleRowChange(row.id, "customerName", e.target.value)
          }
        />
      </td>

      <td className="border p-2 text-center" style={{ width: 140 }}>
        <div className="d-inline-flex gap-2 mb-1 align-items-center">
          <button
            type="button"
            className="btn btn-icon btn-sm btn-soft-danger "
            onClick={() => removeRow(row.id)}
            style={{
              pointerEvents: rows.length === 1 ? "none" : "auto",
              opacity: rows.length === 1 ? 0.5 : 1,
            }}
            aria-label="remove-row"
          >
            <i className="feather-trash" />
          </button>
          {index === rows.length - 1 && (
            <button
              type="button"
              className="btn btn-icon btn-sm btn-soft-info "
              onClick={addRow}
              aria-label="add-row"
            >
              <i className="feather-plus" />
            </button>
          )}
        </div>
      </td>
    </tr>
  ));

  const cardRowElements = rows.map((row, index) => (
    <div key={row.id} style={mobileCardStyle}>
      <div style={{ marginBottom: 8 }}>
        <label style={{ display: "block", width: "100%" }}>
          <small style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            Department
          </small>
          {String(row.department) === String(othersId) ? (
            <input
              type="text"
              className="form-control"
              placeholder="Type department"
              value={row.departmentCustom ?? ""}
              onChange={(e) =>
                handleRowChange(row.id, "departmentCustom", e.target.value)
              }
            />
          ) : (
            <select
              className="form-select"
              value={row.department || ""}
              onChange={(e) => {
                const val = e.target.value;

                handleRowChange(row.id, "department", val);

                if (String(val) !== String(othersId)) {
                  handleRowChange(row.id, "departmentCustom", "");
                }
              }}
            >
              <option value="">Select Department</option>
              {classificationOptionsJSX}
            </select>
          )}
        </label>
      </div>

      <div style={{ marginBottom: 8 }}>
        <label style={{ display: "block", width: "100%" }}>
          <small style={{ display: "block", marginBottom: 6, fontWeight: 600 }}>
            Customer Name
          </small>
          <input
            type="text"
            className="form-control"
            placeholder="Enter customer name"
            value={row.customerName}
            onChange={(e) =>
              handleRowChange(row.id, "customerName", e.target.value)
            }
            style={{ width: "100%" }}
          />
        </label>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
        <button
          type="button"
          className="btn btn-sm btn-soft-danger"
          onClick={() => removeRow(row.id)}
          style={{
            pointerEvents: rows.length === 1 ? "none" : "auto",
            opacity: rows.length === 1 ? 0.5 : 1,
          }}
        >
          <i className="feather-trash" />
        </button>
        {index === rows.length - 1 && (
          <button
            type="button"
            className="btn btn-sm btn-soft-info"
            onClick={addRow}
          >
            <i className="feather-plus" />
          </button>
        )}
      </div>
    </div>
  ));

  const handleSubmit = async () => {
    const payload = buildPayload(formData, rows);
    try {
      const res = await apiRequestCall("post", backendRoute.addUpdateExpances, {
        data: payload,
      });
      if (res?.data?.code === 221) {
        setFormError(parseErrors(res?.data?.errors || {}));
        return;
      }
      if (res?.data) populateFromPayload(res.data);

      setShowSuccess(true);
      setShowMessage(res?.data?.message ?? "Saved successfully.");
    } catch (e) {
      console.error("Save failed", e);
      alert("Save failed (see console)");
    }
  };

  useEffect(() => {
    getCompanyList();
  }, []);

  return (
    <div className="supplier-table-root mt-2">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setFormData((prev: any) => ({ ...prev, department_customers: rows }));
          void handleSubmit();
        }}
      >
        <div className="row g-2">
          <div className="col-md-6">
            <div className="mt-2">
              <small className="mb-1">
                <label style={{ display: "block", fontWeight: 600 }}>
                  Select Company
                </label>
              </small>

              <Select
                placeholder="Select Company"
                name="company_id"
                classNamePrefix="react-select"
                className="tagging"
                options={companyOptions}
                value={selectedOption(companyOptions, formData.company_id)}
                onChange={(s) => handleSelectChange("company_id", s)}
                isClearable
              />
              {formError.company_id && (
                <div className="invalid-feedback d-block text-danger">
                  {formError.company_id}
                </div>
              )}

              {formError.company_id && (
                <div className="invalid-feedback d-block text-danger">
                  {formError.company_id}
                </div>
              )}
            </div>

            {profile?.team_type == "sales" && (
              <div className="mb-2 mt-2">
                <small>
                  <label style={{ display: "block", fontWeight: 600 }}>
                    Concern Person Name
                  </label>
                </small>
                <textarea
                  name="concern_person_name"
                  className="form-control"
                  value={formData.concern_person_name}
                  onChange={inputChange}
                ></textarea>
                {formError.concern_person_name && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.concern_person_name}
                  </div>
                )}
              </div>
            )}

            {profile?.team_type == "sales" && (
              <div className="mb-2">
                <small>
                  <label style={{ display: "block", fontWeight: 600 }}>
                    Mobile
                  </label>
                </small>
                <textarea
                  name="contact_details"
                  className="form-control"
                  value={formData.contact_details}
                  onChange={inputChange}
                ></textarea>
                {formError.contact_details && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.contact_details}
                  </div>
                )}
              </div>
            )}

            {profile?.team_type == "sales" && (
              <div className="mb-2">
                <small>
                  <label style={{ display: "block", fontWeight: 600 }}>
                    Landline
                  </label>
                </small>
                <textarea
                  name="phone_no"
                  className="form-control"
                  value={formData.phone_no}
                  onChange={inputChange}
                ></textarea>
                {formError.phone_no && (
                  <div className="invalid-feedback d-block text-danger">
                    {formError.phone_no}
                  </div>
                )}
              </div>
            )}

            <div className="mb-2 mt-2">
              <small>
                <label style={{ display: "block", fontWeight: 600 }}>
                  Email Id
                </label>
              </small>
              <textarea
                name="email_id"
                className="form-control"
                value={formData.email_id}
                onChange={inputChange}
              ></textarea>
              {formError.email_id && (
                <div className="invalid-feedback d-block text-danger">
                  {formError.email_id}
                </div>
              )}
            </div>
          </div>

          {profile?.team_type == "sales" && (
            <div className="col-md-6">
              <div className="mb-1">
                <small>
                  <label style={{ display: "block", fontWeight: 600 }}>
                    Department & Customer
                  </label>
                </small>

                {size.width > 768 ? (
                  <div
                    className="table-responsive desktop-table"
                    style={{ marginTop: 8 }}
                  >
                    <table className="table table-bordered">
                      <thead>
                        <tr>
                          <th>
                            <small>Department</small>
                          </th>
                          <th>
                            <small>Company Name</small>
                          </th>
                          <th className="text-center" style={{ width: 140 }}>
                            <small>Action</small>
                          </th>
                        </tr>
                      </thead>
                      <tbody>{rowElements}</tbody>
                    </table>
                  </div>
                ) : (
                  <div className="mobile-cards" style={{ marginTop: 8 }}>
                    {cardRowElements}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="d-flex justify-content-end gap-3 mt-4 form-actions">
          <button type="submit" className="btn btn-soft-success">
            Save & Next <i className="ti ti-arrow-right" />
          </button>
        </div>
      </form>

      <Success
        id="OwnerSuccessModal"
        title="Company info"
        message={showMessage}
        show={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          try {
            onNext?.();
          } catch (err) {
            console.error("onNext callback failed", err);
          }
        }}
      />
    </div>
  );
}
